import "lib/@hooks/useEvent"
import { Box, Stack, Typography } from "@mui/material"
import { useEffect, useMemo, useRef, useState } from "react"
import { Bound } from "lib/@components/binding/Bound"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useScheduleEditorIds } from "./hooks/use-schedule-editor-ids"
import { useReferenceState } from "lib/@hooks/use-reference"
import { useRefresh } from "lib/@hooks/useRefresh"
import { VirtualWindow } from "lib/@components/virtual-window/VirtualWindow"
import { getAuthoringScheduleRecommendations } from "./controller/get-authoring-schedule-recommendations"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { AuthoringRecommendationRow } from "./authoring-recommendation-row"
import { If } from "lib/@components/switch"
import { clearFocusedAuthoringRecommendationTarget } from "./lib/focus-authoring-recommendation-target"
import { FitInRemainingSpace } from "lib/@components/fit-in-remaining-space"
import { ensureStyleGuideAnalysis } from "./controller/ensure-style-guide-analysis"
import { AuthoringRecommendationPreviewPopover } from "./authoring-recommendation-preview-popover"
import { resolveAuthoringRecommendationPreview } from "./lib/resolve-authoring-recommendation-preview"
import { resolveAuthoringRecommendationDraft } from "./lib/authoring-schedule-recommendations"
import { AuthoringRecommendationsFilterBar } from "./authoring-recommendations-filter-bar"
import { AuthoringRecommendationsEmptyState } from "./authoring-recommendations-empty-state"
import { useAuthoringRecommendationDisplayList } from "./hooks/use-authoring-recommendation-display-list"
import { ProjectBridgeRecommendationApplied } from "./schedule-editor-event-definitions.mjs"

const RECOMMENDATIONS_TAB_ID = "AuthoringRecommendations"
const RECOMMENDATION_ROW_ESTIMATED_HEIGHT = 116
const RECOMMENDATION_ROW_OVERSCAN = 3

export function AuthoringRecommendationsTab() {
    const { onChange, target, rawSchedule } = useBoundContext()
    const { projectId, scheduleId, draftId } = useScheduleEditorIds()
    const refresh = useRefresh(onChange)
    const [sourceFilter, setSourceFilter] = useReferenceState(getFilterKey(projectId, scheduleId), "all")
    const [currentTab] = useReferenceState("ScheduleRightBarTab", "")
    const [, setSeenIds] = useReferenceState(getSeenKey(projectId, scheduleId), [])
    const [selectedRecommendationId, setSelectedRecommendationId] = useState("")
    const [selectedRecommendationIdsByGroupKey, setSelectedRecommendationIdsByGroupKey] = useState({})
    const [isRefreshingStyleGuide, setIsRefreshingStyleGuide] = useState(false)
    const [previewRecommendationId, setPreviewRecommendationId] = useState("")
    const [previewAnchorEl, setPreviewAnchorEl] = useState(null)
    const previewCloseTimeoutRef = useRef(null)
    const [recommendations, refetchRecommendations] = getAuthoringScheduleRecommendations.useResultsOnce.withRefetch(
        { projectId, scheduleId, sourceFilter },
        withInitialValue(IS_LOADING)
    )
    const draft = resolveAuthoringRecommendationDraft({ target, rawSchedule })
    const recommendationList = useAuthoringRecommendationDisplayList({
        draft,
        recommendations,
        sourceFilter,
        selectedRecommendationIdsByGroupKey,
    })
    const previewRecommendation = useMemo(
        function resolvePreviewRecommendation() {
            return resolveAuthoringRecommendationPreview({
                previewRecommendationId,
                recommendations: recommendationList,
            })
        },
        [previewRecommendationId, recommendationList]
    )
    const recommendationIdsKey = recommendationList.map((item) => item?.id).join("|")

    useEffect(markRecommendationsSeen, [currentTab, recommendationIdsKey, recommendationList, setSeenIds])
    useEffect(clearMissingSelection, [
        recommendationIdsKey,
        recommendationList,
        selectedRecommendationId,
        setSelectedRecommendationId,
    ])
    useEffect(clearMissingPreview, [previewRecommendation, previewRecommendationId])
    useEffect(clearSelectionOnUnmount, [])
    ProjectBridgeRecommendationApplied(draftId).useEvent(refetchRecommendations)

    return (
        <Bound refresh={refresh}>
            <Stack spacing={1.5} sx={{ minHeight: 0, height: "100%", flex: 1, pt: 1 }}>
                <AuthoringRecommendationsFilterBar
                    sourceFilter={sourceFilter}
                    isRefreshingStyleGuide={isRefreshingStyleGuide}
                    onShowAll={handleShowAll}
                    onShowLegislation={handleShowLegislation}
                    onShowReviewProjects={handleShowReviewProjects}
                    onShowProjectBridge={handleShowProjectBridge}
                    onShowStyleGuide={handleShowStyleGuide}
                    onRefreshStyleGuide={handleRefreshStyleGuide}
                />
                <FitInRemainingSpace
                    setHeight
                    leaveSpace={32}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowX: "hidden",
                    }}
                >
                    <If
                        truthy={recommendations === IS_LOADING}
                        then={
                            <Typography variant="body2" color="text.secondary">
                                Loading recommendations...
                            </Typography>
                        }
                        else={
                            <If
                                truthy={recommendationList.length > 0}
                                then={
                                    <Box
                                        data-cy="authoring-recommendations-virtual-list"
                                        sx={{ height: "100%", minHeight: 0, position: "relative" }}
                                    >
                                        <VirtualWindow
                                            list={recommendationList}
                                            itemSize={RECOMMENDATION_ROW_ESTIMATED_HEIGHT}
                                            overscan={RECOMMENDATION_ROW_OVERSCAN}
                                            onVisibleChanged={handleVisibleRecommendationsChanged}
                                            item={
                                                <AuthoringRecommendationRow
                                                    selectedRecommendationId={selectedRecommendationId}
                                                    onSelectRecommendation={handleSelectRecommendation}
                                                    onHoverRecommendation={handleHoverRecommendation}
                                                    onLeaveRecommendation={handleLeaveRecommendation}
                                                    selectedRecommendationIdsByGroupKey={
                                                        selectedRecommendationIdsByGroupKey
                                                    }
                                                    onSelectRecommendationGroupChoice={
                                                        handleSelectRecommendationGroupChoice
                                                    }
                                                />
                                            }
                                        />
                                    </Box>
                                }
                                else={
                                    <AuthoringRecommendationsEmptyState
                                        sourceFilter={sourceFilter}
                                        isRefreshingStyleGuide={isRefreshingStyleGuide}
                                        onRefreshStyleGuide={handleRefreshStyleGuide}
                                    />
                                }
                            />
                        }
                    />
                </FitInRemainingSpace>
                <AuthoringRecommendationPreviewPopover
                    recommendation={previewRecommendation}
                    anchorEl={previewAnchorEl}
                    open={Boolean(previewRecommendation && previewAnchorEl)}
                    onMouseEnter={handlePreviewMouseEnter}
                    onMouseLeave={handlePreviewMouseLeave}
                />
            </Stack>
        </Bound>
    )

    function markRecommendationsSeen() {
        if (currentTab !== RECOMMENDATIONS_TAB_ID || recommendationList.length === 0) return
        setSeenIds((current = []) => mergeSeenIds(current, recommendationList))
    }

    function handleSelectRecommendation(recommendation) {
        setSelectedRecommendationId(recommendation?.id || "")
    }

    function handleSelectRecommendationGroupChoice(groupKey, recommendationId) {
        if (!groupKey) return

        setSelectedRecommendationIdsByGroupKey((current) => ({
            ...current,
            [groupKey]: recommendationId || "",
        }))
    }

    function handleHoverRecommendation(recommendation, anchorEl) {
        clearPreviewCloseTimeout()
        setPreviewRecommendationId(recommendation?.id || "")
        setPreviewAnchorEl(anchorEl || null)
    }

    function handleLeaveRecommendation() {
        queueClosePreview()
    }

    function handleShowAll() {
        setSourceFilter("all")
    }

    function handleShowLegislation() {
        setSourceFilter("legislation")
    }

    function handleShowReviewProjects() {
        setSourceFilter("reviewProject")
    }

    function handleShowStyleGuide() {
        setSourceFilter("styleGuide")
    }

    function handleShowProjectBridge() {
        setSourceFilter("projectBridge")
    }

    async function handleRefreshStyleGuide() {
        if (!projectId || !scheduleId) return
        setIsRefreshingStyleGuide(true)
        try {
            await ensureStyleGuideAnalysis({ projectId, scheduleId, force: true })
            refetchRecommendations()
        } finally {
            setIsRefreshingStyleGuide(false)
        }
    }

    function clearMissingSelection() {
        if (!selectedRecommendationId) return

        const stillVisible = recommendationList.some((item) => item?.id === selectedRecommendationId)
        if (stillVisible) return

        setSelectedRecommendationId("")
        clearFocusedAuthoringRecommendationTarget()
    }

    function clearMissingPreview() {
        if (previewRecommendation) return
        if (!previewRecommendationId) return
        clearPreviewCloseTimeout()
        setPreviewRecommendationId("")
        setPreviewAnchorEl(null)
    }

    function clearSelectionOnUnmount() {
        return () => {
            clearPreviewCloseTimeout()
            clearFocusedAuthoringRecommendationTarget()
        }
    }

    function handlePreviewMouseEnter() {
        clearPreviewCloseTimeout()
    }

    function handlePreviewMouseLeave() {
        queueClosePreview()
    }

    function queueClosePreview() {
        clearPreviewCloseTimeout()
        previewCloseTimeoutRef.current = setTimeout(closePreview, 120)
    }

    function clearPreviewCloseTimeout() {
        if (!previewCloseTimeoutRef.current) return
        clearTimeout(previewCloseTimeoutRef.current)
        previewCloseTimeoutRef.current = null
    }

    function closePreview() {
        clearPreviewCloseTimeout()
        setPreviewRecommendationId("")
        setPreviewAnchorEl(null)
    }

    function handleVisibleRecommendationsChanged(firstVisible, lastVisible) {
        if (!previewRecommendationId) return

        const previewIndex = recommendationList.findIndex(isPreviewRecommendation)
        if (previewIndex < 0) {
            closePreview()
            return
        }

        const firstVisibleIndex = resolveVisibleItemIndex(firstVisible)
        const lastVisibleIndex = resolveVisibleItemIndex(lastVisible)
        if (firstVisibleIndex < 0 || lastVisibleIndex < 0) {
            closePreview()
            return
        }

        const firstRenderedIndex = Math.max(0, firstVisibleIndex - RECOMMENDATION_ROW_OVERSCAN)
        const lastRenderedIndex = Math.min(
            recommendationList.length - 1,
            lastVisibleIndex + RECOMMENDATION_ROW_OVERSCAN
        )
        if (previewIndex >= firstRenderedIndex && previewIndex <= lastRenderedIndex) return

        closePreview()
    }

    function isPreviewRecommendation(item) {
        return item?.id === previewRecommendationId
    }
}

function mergeSeenIds(current, recommendations) {
    return Array.from(
        new Set([
            ...(Array.isArray(current) ? current : []),
            ...recommendations.map((item) => item?.id).filter(Boolean),
        ])
    )
}

function resolveVisibleItemIndex(visibleItem) {
    const index = Number(visibleItem?.key ?? visibleItem?.props?.index)
    if (!Number.isFinite(index)) return -1
    return index
}

function getFilterKey(projectId, scheduleId) {
    return `authoring-recommendations-filter:${projectId}:${scheduleId}`
}

function getSeenKey(projectId, scheduleId) {
    return `authoring-recommendations-seen:${projectId}:${scheduleId}`
}
